import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Receipt as ReceiptIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axiosInstance from '../../services/axiosInstance';

interface OrderItem {
  assetId: {
    _id: string;
    title?: string;
    fileName: string;
  };
  assetName: string;
  storeMaterialArticleNumber?: string;
  materialProductName?: string;
  quantity: number;
  stockAtOrder: number;
}

interface Order {
  _id: string;
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  customerMessage?: string;
  items: OrderItem[];
  status: 'pending' | 'processing' | 'completed' | 'cancelled';
  totalItems: number;
  createdAt: string;
}

const getOrderItemDetails = (item: OrderItem): string => {
  const details: string[] = [];

  if (item.storeMaterialArticleNumber) {
    details.push(`Art.nr Butiksmaterial: ${item.storeMaterialArticleNumber}`);
  }

  if (item.materialProductName) {
    details.push(`Produktnamn material: ${item.materialProductName}`);
  }

  details.push(`Antal: ${item.quantity}`);
  details.push(`Lagersaldo vid beställning: ${item.stockAtOrder}`);

  return details.join(' | ');
};

const OrderManagement: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/cart/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Kunde inte ladda beställningar');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (orderId: string) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, orderId: string) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedOrderId(orderId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedOrderId(null);
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!selectedOrderId) return;
    
    try {
      await axiosInstance.put(`/api/cart/orders/${selectedOrderId}/status`, { status: newStatus });
      setOrders(orders.map(order => 
        order._id === selectedOrderId ? { ...order, status: newStatus as Order['status'] } : order
      ));
      setSnackbar({ open: true, message: `Status ändrad till ${getStatusText(newStatus)}`, severity: 'success' });
    } catch (error) {
      console.error('Error updating order status:', error);
      setSnackbar({ open: true, message: 'Kunde inte uppdatera status', severity: 'error' });
    }
    handleMenuClose();
  };

  const handleDeleteOrder = async () => {
    if (!selectedOrderId) return;
    
    const order = orders.find(o => o._id === selectedOrderId);
    if (!window.confirm(`Är du säker på att du vill ta bort beställning ${order?.orderNumber}?`)) {
      handleMenuClose();
      return;
    }

    try {
      await axiosInstance.delete(`/api/cart/orders/${selectedOrderId}`);
      setOrders(orders.filter(order => order._id !== selectedOrderId));
      setSnackbar({ open: true, message: 'Beställning borttagen', severity: 'success' });
    } catch (error) {
      console.error('Error deleting order:', error);
      setSnackbar({ open: true, message: 'Kunde inte ta bort beställning', severity: 'error' });
    }
    handleMenuClose();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'processing': return 'info';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Väntande';
      case 'processing': return 'Behandlas';
      case 'completed': return 'Slutförd';
      case 'cancelled': return 'Avbruten';
      default: return status;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Laddar beställningar...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <ReceiptIcon sx={{ mr: 2, color: 'primary.main' }} />
        <Typography variant="h5" component="h2">
          Beställningar
        </Typography>
        <Chip 
          label={`${orders.length} beställningar`} 
          color="primary" 
          sx={{ ml: 2 }} 
        />
        <Tooltip title="Uppdatera lista">
          <IconButton onClick={fetchOrders} sx={{ ml: 'auto' }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {orders.length === 0 ? (
        <Alert severity="info">Inga beställningar hittades</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50px"></TableCell>
                <TableCell><strong>Beställningsnummer</strong></TableCell>
                <TableCell><strong>Kund</strong></TableCell>
                <TableCell><strong>E-post</strong></TableCell>
                <TableCell><strong>Antal artiklar</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Datum</strong></TableCell>
                <TableCell width="60px"><strong>Åtgärder</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <React.Fragment key={order._id}>
                  <TableRow 
                    hover 
                    sx={{ cursor: 'pointer' }}
                    onClick={() => toggleExpanded(order._id)}
                  >
                    <TableCell>
                      <IconButton size="small">
                        {expandedOrders.has(order._id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{order.orderNumber}</TableCell>
                    <TableCell>{order.customerName}</TableCell>
                    <TableCell>{order.customerEmail}</TableCell>
                    <TableCell>{order.totalItems}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getStatusText(order.status)} 
                        color={getStatusColor(order.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(order.createdAt).toLocaleDateString('sv-SE', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        size="small" 
                        onClick={(e) => handleMenuOpen(e, order._id)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell 
                      style={{ paddingBottom: 0, paddingTop: 0 }} 
                      colSpan={8}
                    >
                      <Collapse in={expandedOrders.has(order._id)}>
                        <Box sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                          {order.customerMessage && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                Meddelande:
                              </Typography>
                              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                "{order.customerMessage}"
                              </Typography>
                            </Box>
                          )}
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Beställda artiklar:
                          </Typography>
                          <List dense>
                            {order.items.map((item, index) => (
                              <ListItem key={index} sx={{ py: 0.5 }}>
                                <ListItemText
                                  primary={item.assetName}
                                  secondary={getOrderItemDetails(item)}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem disabled sx={{ fontSize: '0.75rem', color: 'text.secondary', py: 0.5 }}>
          Ändra status
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('pending')}>
          <HourglassIcon sx={{ mr: 1, fontSize: 18, color: 'warning.main' }} />
          Väntande
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('processing')}>
          <RefreshIcon sx={{ mr: 1, fontSize: 18, color: 'info.main' }} />
          Behandlas
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('completed')}>
          <CheckCircleIcon sx={{ mr: 1, fontSize: 18, color: 'success.main' }} />
          Slutförd
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('cancelled')}>
          <CancelIcon sx={{ mr: 1, fontSize: 18, color: 'error.main' }} />
          Avbruten
        </MenuItem>
        <MenuItem divider />
        <MenuItem onClick={handleDeleteOrder} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
          Ta bort beställning
        </MenuItem>
      </Menu>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrderManagement; 